import { useEffect, useState } from 'react'

import Checkbox from '@dd3tech/wiggui/Checkbox'
import Flex from '@dd3tech/wiggui/Flex'
import GoogleIcon from '@dd3tech/wiggui/GoogleIcon'
import Select from '@dd3tech/wiggui/Select'
import Text from '@dd3tech/wiggui/Text'
import { composeClasses } from '@wiggot/utils/src/strings'
import { useModalPublishOrUnpublishPropertyStore } from '../../../store/modals/modal-publish-or-unpublish-property.store'
import { useAdOptions } from './hooks/useAdOptions'

interface AdOptionProps {
  img: string
  description?: string
  id: string
  tabSelected: 'associations' | 'portals'
  isDisabledAndChecked?: boolean
  setDisabledConfirmButton?: (value: boolean) => void
  delayedText?: string
  isPendingPublish?: boolean
  isUpdatingPublish?: boolean
  publishIsFailed?: boolean
}

export const AdOption = ({
  img,
  description,
  isDisabledAndChecked,
  id,
  tabSelected,
  setDisabledConfirmButton,
  delayedText,
  isPendingPublish,
  publishIsFailed,
  isUpdatingPublish
}: AdOptionProps) => {
  const selectedAdOptions = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.selectedAdOptions
  )

  const adOptionSelected = selectedAdOptions?.find(
    (selectedAdOption) => selectedAdOption?.portalId === id
  )

  const {
    adOptions,
    isLoading: isLoadingAdOptions,
    isError: isErrorAdOptions
  } = useAdOptions({
    tabSelected,
    portalId: id
  })

  const modalType = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.modalState?.type
  )

  const toggleSelectedPortalId = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.toggleSelectedPortalId
  )

  const toggleAssociation = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.toggleSelectedAssociation
  )

  const handleToggle = () => {
    if (tabSelected === 'associations') {
      toggleAssociation(id)
      return
    }

    toggleSelectedPortalId(id)
  }

  const toggleSelectAdOption = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.toggleSelectAdOption
  )

  const portalIsChecked = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.portalIsChecked
  )

  const associationIsChecked = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.associationIsChecked
  )

  const isChecked =
    tabSelected === 'associations'
      ? associationIsChecked(id)
      : portalIsChecked(id)

  const [openSelect, setOpenSelect] = useState(false)

  useEffect(() => {
    if (isChecked && adOptionSelected?.available === 0) {
      setDisabledConfirmButton?.(true)
      return
    }

    setDisabledConfirmButton?.(false)
  }, [adOptionSelected, setDisabledConfirmButton, isChecked])

  useEffect(() => {
    if (!isChecked && openSelect) {
      setOpenSelect(false)
    }
  }, [isChecked, openSelect])

  return (
    <Flex
      as="button"
      dir="col"
      items="start"
      onClick={() =>
        !isErrorAdOptions &&
        !isDisabledAndChecked &&
        !isPendingPublish &&
        !isUpdatingPublish &&
        handleToggle()
      }
      justify="between"
      gap="gap-4"
      wFull
      className={composeClasses(
        (isDisabledAndChecked ||
          isErrorAdOptions ||
          isPendingPublish ||
          isUpdatingPublish) &&
          '!cursor-default',
        publishIsFailed ? 'border-notif-red' : 'border-surface-strokes',
        'border rounded-xl p-4'
      )}
    >
      <Flex dir="row" items="center" justify="between" gap="gap-6" wFull>
        <Flex dir="col" gap="gap-6" items="start" justify="center" wFull>
          <Checkbox
            isDisabled={isDisabledAndChecked || isErrorAdOptions}
            inputClassName={composeClasses(
              isDisabledAndChecked && '!border-transparent',
              isErrorAdOptions && '!border-surface-gray-20'
            )}
            isChecked={isDisabledAndChecked ? isDisabledAndChecked : isChecked}
            onChange={() => ({})}
            type="checkbox"
          />
          <img
            className="min-w-[100px] md:min-w-[214px]"
            alt="logo"
            src={img}
            width={214}
            height={24}
          />
        </Flex>
        <Flex wFull justify="end" items="center">
          {adOptions?.length === 0 && description && (
            <Text className="text-body-m text-primary max-w-[210px]">
              {description}
            </Text>
          )}
          {modalType === 'publish' &&
            isLoadingAdOptions &&
            adOptions?.length === 0 && (
              <Select
                onClose={() => ({})}
                isOpen={false}
                setIsOpen={() => ({})}
                classNameTrigger="w-[180px] min-w-[180px] md:w-[210px] md:min-w-[210px]"
                classNameContainer="gap-2 px-3"
                isResponsive={false}
                isDisabled={true}
                customTriggerButton={
                  <Flex
                    dir="row"
                    wFull
                    justify="between"
                    items="center"
                    className="border hover:bg-surface-gray-10 border-surface-strokes rounded-lg bg-white py-1.5 px-4"
                  >
                    <Flex dir="col" items="start" justify="start">
                      <Text className="text-body-s text-secondary">
                        Cargando...
                      </Text>
                    </Flex>
                    <GoogleIcon name="expand_more" />
                  </Flex>
                }
              >
                <div />
              </Select>
            )}
          {modalType === 'publish' &&
            adOptions &&
            adOptions?.length === 0 &&
            isErrorAdOptions &&
            !isLoadingAdOptions && (
              <Flex dir="col" items="center" justify="start" gap="gap-1">
                <p className="text-xs text-notif-red">
                  Ocurrio un error al cargar los anuncios del portal
                </p>
              </Flex>
            )}
          {modalType === 'publish' && adOptions && adOptions?.length > 0 && (
            <Flex dir="col" items="center" justify="start" gap="gap-1">
              <Select
                onClose={() => setOpenSelect(false)}
                isOpen={openSelect}
                setIsOpen={setOpenSelect}
                className={composeClasses(
                  (isDisabledAndChecked ||
                    !isChecked ||
                    isPendingPublish ||
                    isUpdatingPublish) &&
                    'pointer-events-none opacity-50'
                )}
                classNameTrigger="w-[180px] min-w-[180px] md:w-[210px] md:min-w-[210px]"
                classNameContainer="gap-2 px-3"
                isResponsive={false}
                customTriggerButton={
                  <Flex
                    dir="row"
                    wFull
                    justify="between"
                    items="center"
                    className={composeClasses(
                      'border hover:bg-surface-gray-10 border-surface-strokes rounded-lg bg-white py-1.5 px-4',
                      isChecked &&
                        !adOptionSelected?.adOptionId &&
                        'border-red-500'
                    )}
                  >
                    <Flex dir="col" items="start" justify="start">
                      <Text className="text-body-s text-secondary">
                        {isDisabledAndChecked
                          ? 'Publicada en portal'
                          : 'Tipo de anuncio'}
                      </Text>
                      {adOptionSelected && (
                        <Text className="line-clamp-2 capitalize">
                          {adOptionSelected?.name ||
                            adOptionSelected?.adOptionId?.toLowerCase()}
                        </Text>
                      )}
                    </Flex>
                    <GoogleIcon
                      name={openSelect ? 'expand_less' : 'expand_more'}
                    />
                  </Flex>
                }
              >
                {adOptions?.map((option) => {
                  const optionIsSelected = Boolean(
                    selectedAdOptions?.find(
                      (selectedAdOption) =>
                        selectedAdOption?.portalId === id &&
                        selectedAdOption?.adOptionId === option?.id
                    )
                  )

                  return (
                    <button
                      type="button"
                      className={composeClasses(
                        optionIsSelected
                          ? 'font-bold pointer-events-none'
                          : 'hover:font-semibold',
                        'w-full text-body-m text-primary text-left capitalize'
                      )}
                      key={option?.id + option?.name}
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleSelectAdOption(
                          id,
                          option?.id,
                          option?.name,
                          option?.available
                        )
                        setOpenSelect(false)
                      }}
                    >
                      {option?.name}
                    </button>
                  )
                })}
              </Select>
              {(adOptionSelected?.available ||
                adOptionSelected?.available === 0) && (
                <Flex
                  dir="row"
                  items="center"
                  justify="start"
                  gap="gap-1"
                  wFull
                  className="pl-4"
                >
                  <GoogleIcon
                    name={adOptionSelected?.available === 0 ? 'error' : 'info'}
                    className={composeClasses(
                      adOptionSelected?.available === 0
                        ? 'text-red-500'
                        : 'text-primary',
                      'text-[12px]'
                    )}
                  />
                  <Text
                    className={composeClasses(
                      adOptionSelected?.available === 0
                        ? 'text-red-500'
                        : 'text-primary',
                      'text-body-s'
                    )}
                  >
                    {adOptionSelected?.available}{' '}
                    {adOptionSelected?.available === 1
                      ? 'anuncio disponible'
                      : 'anuncios disponibles'}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      {delayedText && (
        <Text className="leading-4 font-[467] text-[11px] flex flex-row gap-2 items-center justify-start text-secondary rounded-[4px] bg-surface-gray-10 w-full text-left p-2">
          {delayedText}
        </Text>
      )}
      {publishIsFailed && (
        <Text className="leading-4 font-[467] text-[11px] flex flex-row gap-2 items-center justify-start text-notif-red rounded-[4px] bg-surface-gray-10 w-full text-left p-2">
          La publicación en este portal ha fallado
        </Text>
      )}
    </Flex>
  )
}
